import { parseColor, rgbToHsl, hslToRgb, rgbToHex, type Rgb } from './convert.js';

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function clamp255(n: number): number {
  return Math.max(0, Math.min(255, n));
}

function ensure(color: string): Rgb {
  const rgb = parseColor(color);
  if (!rgb) throw new Error(`Invalid color: ${color}`);
  return rgb;
}

export function lighten(color: string, amount: number): string {
  const hsl = rgbToHsl(ensure(color));
  hsl.l = clamp01(hsl.l + amount);
  return rgbToHex(hslToRgb(hsl));
}

export function darken(color: string, amount: number): string {
  return lighten(color, -amount);
}

export function saturate(color: string, amount: number): string {
  const hsl = rgbToHsl(ensure(color));
  hsl.s = clamp01(hsl.s + amount);
  return rgbToHex(hslToRgb(hsl));
}

export function desaturate(color: string, amount: number): string {
  return saturate(color, -amount);
}

export function rotateHue(color: string, degrees: number): string {
  const hsl = rgbToHsl(ensure(color));
  hsl.h = (hsl.h + degrees + 360) % 360;
  return rgbToHex(hslToRgb(hsl));
}

export function mix(a: string, b: string, weight = 0.5): string {
  const ra = ensure(a);
  const rb = ensure(b);
  const w = clamp01(weight);
  return rgbToHex({
    r: clamp255(ra.r * (1 - w) + rb.r * w),
    g: clamp255(ra.g * (1 - w) + rb.g * w),
    b: clamp255(ra.b * (1 - w) + rb.b * w),
    a: ra.a * (1 - w) + rb.a * w
  });
}

export function invert(color: string): string {
  const rgb = ensure(color);
  return rgbToHex({ r: 255 - rgb.r, g: 255 - rgb.g, b: 255 - rgb.b, a: rgb.a });
}

export function grayscale(color: string): string {
  const rgb = ensure(color);
  const gray = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
  return rgbToHex({ r: gray, g: gray, b: gray, a: rgb.a });
}

export function complement(color: string): string {
  return rotateHue(color, 180);
}

export function triad(color: string): [string, string, string] {
  return [color, rotateHue(color, 120), rotateHue(color, 240)];
}

export function tetrad(color: string): [string, string, string, string] {
  return [color, rotateHue(color, 90), rotateHue(color, 180), rotateHue(color, 270)];
}

export function analogous(color: string, count = 3, spread = 30): string[] {
  const start = -((count - 1) / 2) * spread;
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(rotateHue(color, start + i * spread));
  }
  return out;
}
